import { initializer, inject, injectable } from "@launchtray/tsyringe-async";
import { PromiseCompletionSource } from "../../../../shared/async/PromiseCompletionSource";
import { Viewable } from "../../../../scene/viewable/Viewable";
import { ICanvasScaler } from "../../../../canvas/ICanvasScaler";
import { SoundsResourcePackageWrapper } from "../../common/resources/SoundsResourcePackageWrapper";
import { UserLevelUpResourcePackage } from "./resources/UserLevelUpResourcePackage";
import { UserLevelUpPopupView } from "./view/UserLevelUpPopupView";
import { IUserLevelUpModel } from "./model/IUserLevelUpModel";
import { IUserLevelUpController } from "./controller/IUserLevelUpController";
import { IGuestModel } from "../../common/models/IGuestModel";
import { CommonResourcesPackage } from "../../common/resources/CommonResourcesPackage";
import { IPlayerModel } from "../../common/models/IPlayerModel";
import { UserProfileResourcePackage } from "../userprofile/resources/UserProfileResourcePackage";
import { CrownsResourcePackage } from "../../common/resources/CrownsResourcePackage";
import { AsyncOperationNotification } from "../../common/components/AsyncOperationNotification";

@injectable()
export class UserLevelUpPopupScene extends Viewable<void> {
    private readonly commonResources: CommonResourcesPackage;
    private readonly userProfileResources: UserProfileResourcePackage;
    private readonly soundResources: SoundsResourcePackageWrapper;
    private readonly userLevelUpController: IUserLevelUpController;
    private readonly userLevelUpModel: IUserLevelUpModel;
    private readonly canvasScaler: ICanvasScaler;
    private readonly guestModel: IGuestModel;
    private readonly playerModel: IPlayerModel;
    private readonly asyncOperationNotification: AsyncOperationNotification;

    public constructor(
        commonResources: CommonResourcesPackage,
        userProfileResources: UserProfileResourcePackage,
        soundResources: SoundsResourcePackageWrapper,
        @inject("IUserLevelUpController") userLevelUpController: IUserLevelUpController,
        @inject("IUserLevelUpModel") userLevelUpModel: IUserLevelUpModel,
        @inject("ICanvasScaler") canvasScaler: ICanvasScaler,
        @inject("IGuestModel") guestModel: IGuestModel,
        @inject("IPlayerModel") playerModel: IPlayerModel,
        asyncOperationNotification: AsyncOperationNotification,
    ) {
        super();
        this.commonResources = commonResources;
        this.userProfileResources = userProfileResources;
        this.soundResources = soundResources;
        this.userLevelUpController = userLevelUpController;
        this.userLevelUpModel = userLevelUpModel;
        this.canvasScaler = canvasScaler;
        this.guestModel = guestModel;
        this.playerModel = playerModel;
        this.asyncOperationNotification = asyncOperationNotification;
    }

    @initializer()
    public async initialize(): Promise<void> {
        const userLevelUpResources = new UserLevelUpResourcePackage();
        const crownsResources = new CrownsResourcePackage();
        try {
            this.asyncOperationNotification.show();
            await userLevelUpResources.load();
            await crownsResources.load();
            this.asyncOperationNotification.hide();
            const pcs = new PromiseCompletionSource<void>();
            this.view = new UserLevelUpPopupView(
                this.commonResources,
                userLevelUpResources,
                this.userProfileResources,
                crownsResources,
                this.soundResources,
                this.canvasScaler,
                this.userLevelUpController,
                this.userLevelUpModel,
                this.guestModel,
                this.playerModel,
                pcs
            );
            pcs.result.then(() => {
                userLevelUpResources.destroy();
                crownsResources.destroy();
                this.operationControls.complete();
            }, (reason: any) => {
                userLevelUpResources.destroy();
                crownsResources.destroy();
                this.operationControls.error(reason);
            });
        } catch (error: unknown) {
            if (userLevelUpResources?.loaded) {
                userLevelUpResources.destroy();
            }
            if (crownsResources?.loaded) {
                crownsResources.destroy();
            }
            this.asyncOperationNotification.hide();
            this.operationControls.error(error);
        }
    }
}
